import { useState } from "react";
import { CURRENCY } from "../../../constants";
import { GameModes } from "../../../config";

const PragmaticGameCard = ({ gameData, onPlay }) => {
  const [showActions, setShowActions] = useState(false);

  const handleCardClick = (e) => {
    if (e.target.tagName === "BUTTON") return;
    setShowActions(!showActions);
  };

  // Format bet limits
  const formatBetLimits = (min, max) => {
    if (!min || !max) return null;
    return `${min} - ${max} ${CURRENCY}`;
  };

  // Parse and format last results for roulette tables
  const formatRouletteResults = (results) => {
    if (!results) return [];
    try {
      const parsedResults = JSON.parse(results);
      if (!Array.isArray(parsedResults) || parsedResults.length === 0)
        return [];

      // Return only last 6 results for display
      return parsedResults.slice(0, 6);
    } catch (e) {
      return [];
    }
  };

  // Check if the game is a roulette game
  const isRoulette = gameData.name?.toLowerCase().includes("roulette");

  // Get formatted bet limits
  const betLimits = formatBetLimits(gameData.minBet, gameData.maxBet);

  // Get results for roulette tables
  const rouletteResults = isRoulette
    ? formatRouletteResults(gameData.lastResults)
    : [];

  // Extract player count
  const playerCount =
    gameData.totalSeatedPlayers !== null ? gameData.totalSeatedPlayers : null;

  // Determine the bet range from the image URL (from example image)
  // In a real app you'd get this from API data
  const betRange = "0.5 ETB - 50000 ETB";

  return (
    <div
      className="relative aspect-[4/3] rounded-lg overflow-hidden shadow-lg cursor-pointer transition-transform duration-300 hover:scale-105"
      onClick={handleCardClick}
    >
      {/* Game image */}
      <img
        src={gameData.imagePath}
        alt={gameData.name}
        className="object-cover w-full h-full"
      />

      {/* Top info bar - contains bet range and player count */}
      <div className="absolute top-0 left-0 right-0 flex flex-wrap items-center justify-between bg-black/70 px-2 py-1">
        {/* Bet range indicator */}
        <div className="text-green-400 text-xs font-bold">
          {betLimits || betRange}
        </div>

        {/* Player count */}
        {playerCount !== null && (
          <div className="flex items-center text-xs">
            <svg
              className="w-4 h-4 mr-1 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"></path>
            </svg>
            <span className="text-white font-bold">
              {playerCount}/{gameData.tableId === "545" ? "2000" : "2000"}
            </span>
          </div>
        )}
      </div>

      {/* Game name with dealer name (extracted from image) */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/80 py-2 px-3 flex justify-between items-center">
        <div className="text-white font-medium text-sm truncate">
          {gameData.name}
        </div>
        <div className="text-white font-medium text-sm truncate">
          {/* This would come from API in a real implementation */}
          {gameData.tableId === "201"
            ? "Nial"
            : gameData.tableId === "230"
            ? "Amy"
            : gameData.tableId === "545"
            ? "Rihanna"
            : ""}
        </div>
      </div>

      {/* Results display for roulette tables */}
      {isRoulette && rouletteResults.length > 0 && (
        <div className="absolute bottom-12 left-0 right-0 flex justify-center gap-1 px-2">
          {rouletteResults.map((result, index) => {
            // Determine number color based on standard roulette rules
            const num = parseInt(result);
            let bgColor = "bg-gray-700"; // default for 0
            let textColor = "text-white";

            if (!isNaN(num)) {
              if (num === 0) {
                bgColor = "bg-green-700";
              } else if (
                [
                  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34,
                  36,
                ].includes(num)
              ) {
                bgColor = "bg-red-700";
              } else if (num > 0) {
                bgColor = "bg-black";
              }
            }

            return (
              <div
                key={index}
                className={`${bgColor} ${textColor} w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold`}
              >
                {result}
              </div>
            );
          })}
        </div>
      )}

      {/* Action buttons overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent
                  flex flex-col items-center justify-end p-3 transition-all duration-300
                  ${
                    showActions
                      ? "opacity-100"
                      : "opacity-0 md:hover:opacity-100"
                  }`}
      >
        <div className="flex flex-col gap-2 w-full max-w-full mb-8">
          {/* Play button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPlay(GameModes.REAL);
            }}
            className="bg-buttonGradient text-white font-medium
                     py-2 px-4 rounded-md
                     hover:from-blue-700 hover:to-purple-700
                     active:from-blue-800 active:to-purple-800
                     transition-all shadow-md hover:shadow-lg
                     text-xs uppercase tracking-wide
                     focus:outline-none focus:ring-2 focus:ring-purple-400
                     w-full flex items-center justify-center"
          >
            Play
          </button>
        </div>
      </div>
    </div>
  );
};

export default PragmaticGameCard;
